/*
 * Copyright (c) 2019 brightlamp
 */

import { axiosInit } from './axiosSetup'
const clonedeep = require('lodash.clonedeep');
import { isBeforeCutoff } from './common'

const resultTypes =  [
    'Constrict',
    'Full Spectrum w/Flash',
    'Full Spectrum w/Pulse',
    'No Light',
    'Full Spectrum w/Flash + Ambient',
    'Full Spectrum w/Pulse + Ambient'
];

const parameterNames = [
    'Average Constriction Speed', // 0
    'Average Diameter',  // 1
    'Average Dilation Velocity', // 2
    'Constriction Time', // 3
    'Latency', // 4
    'Maximum Constriction Speed', // 5
    'Maximum Diameter', // 6
    'Minimum Diameter', // 7
    'Pupillogram', // 8
    '75% Recovery Time', // 9
    'Amplitude', // 10
    'Release Amplitude', // 11
    'Alpha Omega Pupil', // 12
    'Neuro CDS Score', // 13
    'Reflex Score' // 14
];

const eyes = [
    'Both',
    'Right',
    'Left'
];

export default
{
    namespaced: true,
    state:
    {
        tests: [],
        patientData: null,
        patientDataToMutate: null,
        concussionDataToMutate: null,
        normalizedData: localStorage.getItem('normalizedData') ? JSON.parse(localStorage.getItem('normalizedData')) : null,
        pupillogramNormalCurveBounds: localStorage.getItem('pupillogramNormalCurveBounds') ? JSON.parse(localStorage.getItem('pupillogramNormalCurveBounds')) : null,
        patientFileUrl: null
    },
    mutations:
    {
        SET_TESTS: (state, payload) =>
        {
            state.tests = payload
        },
        CLEAR_TESTS(state)
        {
            state.tests = []
        },
        INIT_PATIENT_DATA: (state) =>
        {
            state.patientDataToMutate =
            {
                mrn: '',
                groupId: '',
                name: '',
                dob: null,
                gender: '',
                contactInfo:
                {
                    emailAddress: '',
                    phoneNumber: ''
                }
            }
        },
        UPDATE_MUTATED_PATIENT_DATA: (state, payload) =>
        {
            state.patientDataToMutate = clonedeep(payload)
        },
        UPDATE_MUTATED_CONCUSSION_DATA: (state, payload) =>
        {
            state.concussionDataToMutate = clonedeep(payload)
        },
        SET_PATIENT_DATA: (state, payload) =>
        {
            state.patientData = payload
        },
        SET_NORMALIZED_DATA: (state, payload) =>
        {
            if (payload)
            {
                payload.lastNormalizedDataUpdate = new Date().toISOString();
                state.normalizedData = payload
            }
            else
            {
                state.normalizedData.lastNormalizedDataUpdate = new Date().toISOString()
            }
            localStorage.setItem('normalizedData', JSON.stringify(state.normalizedData))
        },
        SET_PUPILLOGRAM_NORMAL_CURVE_BOUNDS: (state, payload) =>
        {
            if (payload)
            {
                payload.lastPupillogramNormalCurveBounds = new Date().toISOString();
                state.pupillogramNormalCurveBounds = payload
            }
            else
            {
                state.pupillogramNormalCurveBounds.lastPupillogramNormalCurveBounds = new Date().toISOString()
            }
            localStorage.setItem('pupillogramNormalCurveBounds', JSON.stringify(state.pupillogramNormalCurveBounds))
        },
        UPDATE_TEST_PAIR_FOR_URL: (state, payload) =>
        {
            let matchCount = 0;
            for (let test of state.tests)
            {
                if (test.eyes[0].visitId === payload.visit.visitId)
                {
                    matchCount++;
                    test.eyes[0].eyeVideoFileUrl = payload.visit.eyeVideoFileUrl ? payload.visit.eyeVideoFileUrl : 'HTTPS://'
                }
                else if (2 === test.eyes.length && test.eyes[1].visitId === payload.visit.visitId)
                {
                    matchCount++;
                    test.eyes[1].eyeVideoFileUrl = payload.visit.eyeVideoFileUrl ? payload.visit.eyeVideoFileUrl : 'HTTPS://'
                }
                if (test.eyes[0].visitId === payload.visitPair?.visitId)
                {
                    matchCount++;
                    test.eyes[0].eyeVideoFileUrl = payload.visitPair.eyeVideoFileUrl ? payload.visitPair.eyeVideoFileUrl : 'HTTPS://'
                }
                else if (2 === test.eyes.length && test.eyes[1].visitId === payload.visitPair?.visitId)
                {
                    matchCount++;
                    test.eyes[1].eyeVideoFileUrl = payload.visitPair.eyeVideoFileUrl ? payload.visitPair.eyeVideoFileUrl : 'HTTPS://'
                }
                // Break out when we found what we were looking for
                if ((payload.visitPair && 2 === matchCount) || (!payload.visitPair && 1 === matchCount))
                {
                    break
                }
            }
        },
        UPDATE_TEST_PAIR_FOR_BASELINE: (state, payload) =>
        {
            const visit = payload.eyes[0];
            let visitPair
            if (payload.eyes.length > 1)
            {
                visitPair = payload.eyes[1];
            }
            let matchCount = 0;
            for (let test of state.tests)
            {
                if (test.eyes[0].visitId === visit.visitId)
                {
                    matchCount++;
                    test.eyes[0].baseline = visit.baseline;
                    visit.reflexScore = visit.rawReflexScore;
                    const {reflexScoreShortEye, reflexScoreColor, reflexScore} = setScoreElements(visit)
                    test.eyes[0].reflexScoreShortEye = reflexScoreShortEye;
                    test.eyes[0].reflexScoreColor = reflexScoreColor;
                    test.eyes[0].reflexScore = reflexScore
                }
                else if (2 === test.eyes.length && test.eyes[1].visitId === visit.visitId)
                {
                    matchCount++;
                    test.eyes[1].baseline = visit.baseline;
                    visitPair.reflexScore = visit.rawReflexScore;
                    const {reflexScoreShortEye, reflexScoreColor, reflexScore} = setScoreElements(visitPair)
                    test.eyes[1].reflexScoreShortEye = reflexScoreShortEye;
                    test.eyes[1].reflexScoreColor = reflexScoreColor;
                    test.eyes[1].reflexScore = reflexScore
                }
                if (test.eyes[0].visitId === visitPair?.visitId)
                {
                    matchCount++;
                    test.eyes[0].baseline = visitPair.baseline;
                    visit.reflexScore = visitPair.rawReflexScore;
                    const {reflexScoreShortEye, reflexScoreColor, reflexScore} = setScoreElements(visit)
                    test.eyes[0].reflexScoreShortEye = reflexScoreShortEye;
                    test.eyes[0].reflexScoreColor = reflexScoreColor;
                    test.eyes[0].reflexScore = reflexScore
                }
                else if (2 === test.eyes.length && test.eyes[1].visitId === visitPair?.visitId)
                {
                    matchCount++;
                    test.eyes[1].baseline = visitPair.baseline;
                    visitPair.reflexScore = visitPair.rawReflexScore;
                    const {reflexScoreShortEye, reflexScoreColor, reflexScore} = setScoreElements(visitPair)
                    test.eyes[1].reflexScoreShortEye = reflexScoreShortEye;
                    test.eyes[1].reflexScoreColor = reflexScoreColor;
                    test.eyes[1].reflexScore = reflexScore
                }
                // Break out when we found what we were looking for
                if ((visitPair && 2 === matchCount) || (!visitPair && 1 === matchCount))
                {
                    break
                }
            }
        },
        SET_PATIENT_FILE_URL: (state, payload) =>
        {
            state.patientFileUrl = payload
        }
    },
    actions:
    {
        GET_TESTS: async (context, payload) =>
        {
            let lastUpdatedDate;
            // Mark the last update date if we are loading the same MRN or same MRN with same user ID (For researching test)
            if (context.state.tests.length > 0 && payload.mrn === context.state.tests[0].mrn && (!payload.uId || (payload.uId && payload.uId === context.state.tests[0].createdBy)))
            {
                lastUpdatedDate = context.state.tests[0].lastUpdatedDate
            }
            const axios = await axiosInit();
            const response = await axios.get(`/patient-info-reflex/${payload.mrn}`,
     {
                params:
                {
                    uId: payload.uId ? payload.uId : undefined,
                    updatedAt: lastUpdatedDate
                }
            });
            if (200 === response.status)
            {
                const patient = response.data;

                // Pull out just the patient data
                storePatientDataOnly(context, patient);

                const pairedTests = pairTests(patient.visits);
                let tests = [];
                for (const visit of pairedTests)
                {
                    const testParent = {};
                    // Add the MRN so we can use it later to track who this test belongs to
                    testParent.mrn = patient.mrn;
                    // Add the last retrieval time so we can used cached data
                    testParent.lastUpdatedDate = new Date().toISOString();
                    testParent.createdBy = visit[0].createdBy;
                    testParent.groupId = patient.groupId ? patient.groupId : 'N/A';
                    testParent.name = patient.name ? patient.name : 'N/A';
                    testParent.age = returnAge(patient.dob);
                    testParent.dob = patient.dob;
                    testParent.gender = patient.gender ? ('MALE' === patient.gender ? 'Male' : 'Female') : 'N/A';
                    testParent.displayName = visit[0].displayName ? visit[0].displayName : 'N/A';
                    testParent.visitDateAsDate = new Date(visit[0].visitDate);
                    testParent.visitDate = testParent.visitDateAsDate.toLocaleString();
                    testParent.protoTag = visit[0].metaData.protoTag ? visit[0].metaData.protoTag : 'N/A';
                    testParent.operationalMode = visit[0].operationalMode;
                    testParent.eyeLocation = 'PLR' === testParent.operationalMode ? visit.length > 1 ? 'Both' : true === visit[0].rightEye ? 'Right' : 'Left' : 'N/A';
                    testParent.testToken = visit[0].testToken;
                    testParent.beenViewed = visit[0].beenViewed;
                    testParent.showNotViewedDot = testParent.testToken && !testParent.beenViewed ? true : false // Needed to be explicitly set to true/false
                    if (visit[0].concussionData)
                    {
                        testParent.concussionData = visit[0].concussionData
                    }
                    else
                    {
                        testParent.concussionData = Object.assign({}, {asymptomatic: true, concussionDate: null, glasgowComaScale: 0})
                    }
                    for (const visitEye of visit)
                    {
                        visitEye.baseline = visitEye.baseline ? 'Yes' : 'No';
                        visitEye.eyeLocation = 'PLR' === testParent.operationalMode ? true === visitEye.rightEye ? 'Right' : 'Left' : 'N/A';
                        if ('PLR' === testParent.operationalMode)
                        {
                            const actualVideoDuration = visitEye.metaData.videoDuration - visitEye.metaData.illuminationDelay
                            // If No light
                            if (false === visitEye.metaData.flashOn && 0 === visitEye.metaData.torchLevel)
                            {
                                visitEye.resultTypeIndex = 3;
                                visitEye.resultType = resultTypes[visitEye.resultTypeIndex]
                            }
                            // If full spectrum with flash
                            else if (true === visitEye.metaData.flashOn)
                            {
                                visitEye.resultTypeIndex = visitEye.metaData.ambientLevel > 0 ? 4 : 1;
                                visitEye.resultType = resultTypes[visitEye.resultTypeIndex]
                            }
                            // If full spectrum with torch
                            else if (false === visitEye.metaData.flashOn && visitEye.metaData.torchDuration < actualVideoDuration)
                            {
                                visitEye.resultTypeIndex = visitEye.metaData.ambientLevel > 0 ? 5 : 2;
                                visitEye.resultType = resultTypes[visitEye.resultTypeIndex]
                            }
                            else // Constrict
                            {
                                visitEye.resultTypeIndex = 0;
                                visitEye.resultType = resultTypes[visitEye.resultTypeIndex]
                            }
                            visitEye.amp = visitEye.amp ? formatNumber(visitEye.amp) : null;
                            visitEye.avgConSpd = visitEye.avgConSpd ? formatNumber(visitEye.avgConSpd) : null;
                            visitEye.avgDiamtr = visitEye.avgDiamtr ? formatNumber(visitEye.avgDiamtr) : null;
                            visitEye.dilationVel = visitEye.dilationVel ? formatNumber(visitEye.dilationVel) : null;
                            visitEye.timeToMnDiamtr = visitEye.timeToMnDiamtr ? formatNumber(visitEye.timeToMnDiamtr) : null;
                            visitEye.latncy = visitEye.latncy ? formatNumber(visitEye.latncy) : null;
                            visitEye.mxConSpd = visitEye.mxConSpd ? formatNumber(visitEye.mxConSpd) : null;
                            visitEye.mxDiamtr = visitEye.mxDiamtr ? formatNumber(visitEye.mxDiamtr) : null;
                            visitEye.mnDiamtr = visitEye.mnDiamtr ? formatNumber(visitEye.mnDiamtr) : null;
                            visitEye.ramp = visitEye.ramp ? formatNumber(visitEye.ramp) : null;
                            visitEye.recTime75 = visitEye.recTime75 ? formatNumber(visitEye.recTime75) : null;
                            visitEye.canToggleBaseline = canTestBeBaseline(visitEye)

                            // Store the original raw score to be used when toggling the baseline
                            visitEye.rawReflexScore = undefined === visitEye.rawReflexScore ? visitEye.reflexScore : visitEye.rawReflexScore;
                            // Set the reflex score to the original raw value as a reset in case we are toggling the baseline
                            visitEye.reflexScore = visitEye.rawReflexScore;
                            const {reflexScoreShortEye, reflexScore, reflexScoreColor} = setScoreElements(visitEye)
                            visitEye.reflexScoreShortEye = reflexScoreShortEye;
                            visitEye.reflexScore = reflexScore;
                            visitEye.reflexScoreColor = reflexScoreColor;

                            visitEye.concussionCdsScore = visitEye.concussCdsScore != null ? visitEye.concussCdsScore : 'N/A';
                            visitEye.concussionCdsColor = concussionCdsScoreColor(visitEye.concussionCdsScore);
                            visitEye.anisocoria = visitEye.anisocoria ? formatNumber(visitEye.anisocoria) : null;
                            visitEye.miosis = visitEye.miosis ? formatNumber(visitEye.miosis) : null;
                            visitEye.mydriasis = visitEye.mydriasis ? formatNumber(visitEye.mydriasis) : null;

                            if (!visitEye.concussionData)
                            {
                                visitEye.concussionData = Object.assign({}, {asymptomatic: true, concussionDate: null, glasgowComaScale: 0})
                            }
                        }
                        else // NPC mode
                        {
                            visitEye.resultType = 'NPC';
                            visitEye.reflexScoreShortEye = 'N/A';
                            visitEye.reflexScore = null;
                            visitEye.reflexScoreColor = '#000000'
                        }
                    }
                    testParent.resultTypeIndex = visit[0].resultTypeIndex;
                    testParent.eyes = visit;
                    tests.push(testParent)
                }
                context.commit('SET_TESTS', tests)
            }
        },
        GET_TEST_VIDEO_PAIR: async (context, payload) =>
        {
            const axios = await axiosInit();
            const response = await axios.get(`/video-url/${payload.mrn}`,
            {
                params:
                {
                    visitId: payload.visitId,
                    uId: payload.uId ? payload.uId : undefined
                }
            });
            context.commit('UPDATE_TEST_PAIR_FOR_URL', response.data)
        },
        DELETE_TEST: async (context, payload) =>
        {
            const axios = await axiosInit();
            await axios.delete(`/patient-info-reflex-v3/${payload.mrn}/${payload.visitId}`,
            {
                params:
                {
                    uId: payload.uId ? payload.uId : undefined
                }
            })
        },
        UPSERT_NOTE: async (context, payload) =>
        {
            const axios = await axiosInit();
            await axios.post(`/patient-note/${payload.mrn}/${payload.visitId}`, payload.data)
        },
        UPDATE_CONCUSSION_DATA: async (context, payload) =>
        {
            const axios = await axiosInit();
            await axios.post(`/patient-concussion/${payload.mrn}/${payload.visitId}`, payload.data)
        },
        TOGGLE_BASELINE_SETTING: async (context, payload) =>
        {
            const axios = await axiosInit();
            await axios.post(`/patient-baseline/${payload.mrn}/${payload.visitId}`, payload.data)
        },
        GET_NORMALIZED_DATA: async (context) =>
        {
            // If less than 12 hours since we checked, skip server call
            if (context.state.normalizedData && isBeforeCutoff(context.state.normalizedData.lastNormalizedDataUpdate, 12))
            {
                return
            }
            try
            {
                const axios = await axiosInit();
                const response = await axios.get('/app-data/?dataKey=normalized-parameters-v2',
                {
                    params:
                    {
                        updatedAt: context.state.normalizedData ? context.state.normalizedData.lastNormalizedDataUpdate : null
                    }
                });
                context.commit('SET_NORMALIZED_DATA', response.data)
            }
            catch (error)
            {
                // Don't show a 404 error as that just means to use the cached version
                if (error.response.status !== 404)
                {
                    throw error
                }
                // Just update the last time checked
                context.commit('SET_NORMALIZED_DATA')
            }
        },
        GET_PUPILLOGRAM_NORMAL_CURVE_BOUNDS: async (context) =>
        {
            // If less than 12 hours since we checked, skip server call
            if (context.state.pupillogramNormalCurveBounds && isBeforeCutoff(context.state.pupillogramNormalCurveBounds.lastPupillogramNormalCurveBounds, 12))
            {
                return
            }
            try
            {
                const axios = await axiosInit();
                const response = await axios.get('/app-data/?dataKey=pupillogram-normal-curve-bounds',
                {
                    params:
                    {
                        updatedAt: context.state.pupillogramNormalCurveBounds ? context.state.pupillogramNormalCurveBounds.lastPupillogramNormalCurveBounds : null
                    }
                });
                context.commit('SET_PUPILLOGRAM_NORMAL_CURVE_BOUNDS', response.data)
            }
            catch (error)
            {
                // Don't show a 404 error as that just means to use the cached version
                if (error.response.status !== 404)
                {
                    throw error
                }
                // Just update the last time checked
                context.commit('SET_PUPILLOGRAM_NORMAL_CURVE_BOUNDS')
            }
        },
        SAVE_PATIENT_DATA: async (context, payload) =>
        {
            const axios = await axiosInit();
            const response = await axios.post('/patient-info-reflex-v2', payload.data,
            {
                params:
                {
                    oldMrn: payload.oldMrn
                }
            })
            if (response.data.reflexError)
            {
                const code = response.data.reflexError.code;
                if (1 === code)
                {
                    throw {code: code, message: 'The Patient ID you assigned is already in use. Please choose another.'}
                }
            }
            else // Save patient only data portion of the response
            {
                storePatientDataOnly(context, response.data)
            }
        },
        SET_TEST_VIEWED_NO_WAIT: async (context, payload) =>
        {
            const axios = await axiosInit();
            axios.post(`/telehealth/test-request/${payload.testToken}`, null,
            {
                params:
                {
                    visitId: payload.visitId,
                    visitIdPair: payload.visitIdPair
                }
            }).catch()
        },
        GET_PATIENT_FILE_URL: async (context) =>
        {
            const axios = await axiosInit();
            const response = await axios.get('patient-file-url');
            context.commit('SET_PATIENT_FILE_URL', response.data.url)
        },
        UPLOAD_PATIENT_FILE: async (context, payload) =>
        {
            const axios = await axiosInit(true, true);
            await axios.put(payload.url, payload.file)
        }
    }
}

function setScoreElements(visitEye)
{
    if (visitEye.reflexScore && 0 === visitEye.errCode)
    {
        return {
            reflexScoreShortEye: !visitEye.reflexScore.startsWith('Error:') ? `${visitEye.reflexScore[0]} ${visitEye.eyeLocation}` : visitEye.eyeLocation,
            reflexScoreColor: 'Yes' === visitEye.baseline ? '#E65100' : testColor(!visitEye.reflexScore.startsWith('Error:') ? visitEye.reflexScore[0] : ''),
            reflexScore: !visitEye.rawReflexScore.startsWith('Error:') ? `${visitEye.rawReflexScore} out of 5` : visitEye.rawReflexScore.split(':')[1].trim()
        }
    }
    else if ('Yes' === visitEye.baseline && 0 === visitEye.errCode)
    {
        return {
            reflexScoreShortEye: `B ${visitEye.eyeLocation}`,
            reflexScoreColor: '#E65100',
            reflexScore: null
        }
    }
    else if (visitEye.errCode !== 0)
    {
        return {
            reflexScoreShortEye: `E ${visitEye.eyeLocation}`,
            reflexScoreColor: '#000000',
            reflexScore: null
        }
    }
    else
    {
        return {
            reflexScoreShortEye: visitEye.eyeLocation,
            reflexScoreColor: '#000000',
            reflexScore: null
        }
    }
}

function storePatientDataOnly(context, patient)
{
    // Pull out just the patient data
    const patientData =
    {
        mrn: patient.mrn,
        groupId: patient.groupId,
        name: patient.name,
        dob: patient.dob,
        gender: patient.gender,
        contactInfo: {emailAddress: '', phoneNumber: ''}
    };
    if (patient.contactInfo)
    {
        patientData.contactInfo.emailAddress = patient.contactInfo.emailAddress;
        patientData.contactInfo.phoneNumber = patient.contactInfo.phoneNumber
    }
    context.commit('SET_PATIENT_DATA', patientData)
}

function pairTests(patientVisitsIn)
{
    let skipNext = false;
    let patientVisits = [];

    // Sort the patient tests in ascending order
    let sortedPatientVisits = patientVisitsIn
    sortedPatientVisits.sort((a, b) =>
    {
        let comparison = 0;
        if (a.visitDate < b.visitDate)
        {
            comparison = -1;
        }
        else if (a.visitDate > b.visitDate)
        {
            comparison = 1;
        }
        return comparison;
    });

    for (let index = 0; index < sortedPatientVisits.length; ++index)
    {
        let patientVisit = [];
        if (skipNext)
        {
            skipNext = false;
            continue;
        }
        patientVisit.push(sortedPatientVisits[index]);
        // If there is at least one more test remaining to check
        if (index + 1 < sortedPatientVisits.length)
        {
            // If the next test is the pair of the current one
            if (sortedPatientVisits[index].visitIdPair === sortedPatientVisits[index + 1].visitId)
            {
                patientVisit.push(sortedPatientVisits[index + 1]);
                skipNext = true;
            }
        }
        patientVisits.push(patientVisit);
    }
    // Sort the patient tests in descending order
    patientVisits.sort((a, b) =>
    {
        let comparison = 0;
        if (b[0].visitDate < a[0].visitDate)
        {
            comparison = -1;
        }
        else if (b[0].visitDate > a[0].visitDate)
        {
            comparison = 1;
        }
        return comparison;
    });
    return patientVisits;
}

function testColor(reflexScore)
{
    if ('' === reflexScore)
    {
        return '#000000'
    }
    if ('0' === reflexScore)
    {
        return '#F74859'
    }
    if ('1' === reflexScore)
    {
        return '#DB3490'
    }
    if ('2' === reflexScore)
    {
        return '#BA3FEB'
    }
    if ('3' === reflexScore)
    {
        return '#4982FF'
    }
    if ('4' === reflexScore)
    {
        return '#0EBDD3'
    }
    return '#2AC67A'
}

function concussionCdsScoreColor(concussCdsScore)
{
    let score = concussCdsScore.split(' ');
    // If we do not have a score
    if (1 === score.length)
    {
        return '#FFFFFF'
    }
    const scoreInt = parseInt(score[0])
    if (null == scoreInt)
    {
        return '#FFFFFF'
    }
    if (scoreInt < 33)
    {
        return '#F74859'
    }
    if (scoreInt < 67)
    {
        return '#FFFF00'
    }
    return '#2AC67A'
}

function formatNumber(number, digits = 3)
{
    const exponentPos = 3 ===digits ? 'e+3' : 'e+2';
    const exponentNeg = 3 ===digits ? 'e-3' : 'e-2';
    return +(Math.round(number + exponentPos) + exponentNeg)
}

function returnAge(dob, compareDate)
{
    if (!dob)
    {
        return 0
    }
    const compareDateAsDate = compareDate ? compareDate : new Date();
    const age = compareDateAsDate.getFullYear() - new Date(dob).getFullYear();

    if (age < 0 )
    {
        return 0
    }

    if (isNaN(age) || age > 130)
    {
        return null
    }
    return age
}

function returnTorchLevelIndex(resultTypeIndex, torchLevel)
{
    return 1 === resultTypeIndex || 4 === resultTypeIndex || (torchLevel ? torchLevel : 0) <= 0.5 ? 0 : 1
}

function canTestBeBaseline(visitEye)
{
    return 3 === visitEye.resultTypeIndex ||
        (0 === visitEye.resultTypeIndex && visitEye.amp != null && visitEye.mxConSpd != null && visitEye.avgConSpd != null) ||
        (visitEye.resultTypeIndex !== 3 && visitEye.resultTypeIndex !== 0 && visitEye.mxConSpd != null && visitEye.avgConSpd != null && visitEye.recTime75 != null && visitEye.dilationVel != null && visitEye.amp != null)
}

export { resultTypes, parameterNames, eyes, formatNumber, returnAge, returnTorchLevelIndex }